import { useState, useEffect } from 'react';
// 👇 در مسیر vendor، ما از همان توابع api سوپر ادمین استفاده میکنیم چون منطقش یکسان است
import { getSuggestedCaps } from '@/features/vendor/referral-simulator/api';
import ScenarioSelector from '@/features/vendor/referral-simulator/components/ScenarioSelector';

const DEFAULTS = {
    sales_amount: 50000000,
    margin_percent: 30,
    min_margin_percent: 3,
    max_depth: 5,
    safety_factor: 0.5,
    max_sales_percent: 5,
    distribution: 'halving',
};

export default function VendorSimulatorForm({
                                                onSubmit,
                                                onCompare,
                                                loading,
                                                onValidationError,
                                                onOpenItemsModal,     // 👈 پراپ جدید
                                                externalSalesAmount   // 👈 پراپ جدید
                                            }) {
    const [values, setValues] = useState(DEFAULTS);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedCap, setSelectedCap] = useState(null);
    const [showManual, setShowManual] = useState(false);
    const [manualValue, setManualValue] = useState('5');
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);

    // 👇 هر زمان مبلغ از مودال انتخاب اقلام آمد، در فیلد قرار میگیرد
    useEffect(() => {
        if (externalSalesAmount !== undefined && externalSalesAmount !== null) {
            setValues((prev) => ({ ...prev, sales_amount: externalSalesAmount }));
        }
    }, [externalSalesAmount]);

    useEffect(() => {
        const fetchSuggestions = async () => {
            const margin = Number(values.margin_percent);
            if (!margin || margin <= 0) {
                setSuggestions([]);
                return;
            }
            setSuggestionsLoading(true);
            try {
                const data = await getSuggestedCaps(
                    margin,
                    Number(values.min_margin_percent) || 3
                );
                setSuggestions(data.suggestions || []);
                if (selectedCap === null && data.suggestions?.length > 0) {
                    const balanced = data.suggestions.find((s) => s.name === 'متعادل');
                    const initial = balanced || data.suggestions[0];
                    setSelectedCap(initial.percent);
                    setValues((prev) => ({
                        ...prev,
                        max_sales_percent: Number(initial.percent),
                    }));
                }
            } catch {
                // ignore
            } finally {
                setSuggestionsLoading(false);
            }
        };
        fetchSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.margin_percent, values.min_margin_percent]);

    const update = (key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleSelectCap = (percent) => {
        setSelectedCap(percent);
        setValues((prev) => ({ ...prev, max_sales_percent: Number(percent) }));
    };

    const handleToggleManual = () => {
        if (!showManual) {
            setManualValue(String(selectedCap ?? values.max_sales_percent));
        }
        setShowManual((v) => !v);
    };

    const handleManualChange = (val) => {
        setManualValue(val);
        setSelectedCap(null);
        setValues((prev) => ({ ...prev, max_sales_percent: Number(val) }));
    };

    const validate = () => {
        const salesAmount = Number(values.sales_amount);
        if (isNaN(salesAmount) || salesAmount <= 0) {
            return 'مبلغ سبد کالا باید بزرگ‌تر از صفر باشد.';
        }

        const margin = Number(values.margin_percent);
        if (isNaN(margin) || margin < 0 || margin > 100) {
            return 'حاشیه سود باید بین ۰ تا ۱۰۰ درصد باشد.';
        }

        const minMargin = Number(values.min_margin_percent);
        if (isNaN(minMargin) || minMargin < 0 || minMargin > 100) {
            return 'حداقل حاشیه سود باید بین ۰ تا ۱۰۰ درصد باشد.';
        }

        const depth = Number(values.max_depth);
        if (isNaN(depth) || depth < 1) {
            return 'حداکثر عمق زنجیره باید حداقل ۱ باشد.';
        }
        if (depth > 10) {
            return `حداکثر عمق زنجیره ${depth} معتبر نیست. بیشترین مقدار مجاز ۱۰ است.`;
        }

        const safety = Number(values.safety_factor);
        if (isNaN(safety) || safety <= 0 || safety > 1) {
            return 'حداکثر درصد پرداخت از سود باید بین ۰ تا ۱ باشد (مثلاً ۰.۵ = ۵۰٪).';
        }

        const maxSales = Number(values.max_sales_percent);
        if (isNaN(maxSales) || maxSales <= 0 || maxSales > 100) {
            return 'حداکثر مجموع پورسانت باید بین ۰ تا ۱۰۰ درصد باشد.';
        }

        return null;
    };

    const buildPayload = () => ({
        ...values,
        sales_amount: Number(values.sales_amount),
        margin_percent: Number(values.margin_percent),
        min_margin_percent: Number(values.min_margin_percent),
        max_depth: Number(values.max_depth),
        safety_factor: Number(values.safety_factor),
        max_sales_percent: Number(values.max_sales_percent),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const error = validate();
        if (error) {
            onValidationError(error);
            return;
        }
        onSubmit(buildPayload());
    };

    const handleCompare = () => {
        const error = validate();
        if (error) {
            onValidationError(error);
            return;
        }
        onCompare(buildPayload());
    };

    return (
        <form onSubmit={handleSubmit} className="simulator-form" noValidate>
            <div className="form-row">
                <div className="form-col">
                    <label>
                        <span>مبلغ سبد کالا (ریال)</span>
                        {/* 👇 قرارگیری دکمه در کنار فیلد */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                            <input
                                type="number"
                                value={values.sales_amount}
                                onChange={(e) => update('sales_amount', e.target.value)}
                                step="any"
                                style={{ flex: 1, margin: 0 }}
                            />
                            <button
                                type="button"
                                onClick={onOpenItemsModal}
                                style={{
                                    padding: '0 12px',
                                    backgroundColor: '#eff6ff',
                                    color: '#2563eb',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.85rem',
                                    fontFamily: 'inherit'
                                }}
                            >
                                انتخاب اقلام
                            </button>
                        </div>
                        <small>مبلغ کل سفارش خریدار.</small>
                    </label>
                </div>

                <div className="form-col">
                    <label>
                        <span>حاشیه سود (%)</span>
                        <input
                            type="number"
                            value={values.margin_percent}
                            onChange={(e) => update('margin_percent', e.target.value)}
                            step="0.1"
                        />
                        <small>درصد سود خالص از فروش.</small>
                    </label>
                </div>

                <div className="form-col">
                    <label>
                        <span>حداقل حاشیه سود (%)</span>
                        <input
                            type="number"
                            value={values.min_margin_percent}
                            onChange={(e) => update('min_margin_percent', e.target.value)}
                            step="0.1"
                        />
                        <small>حداقل سود قابل قبول پلتفرم.</small>
                    </label>
                </div>
            </div>

            <div className="form-row">
                <div className="form-col">
                    <label>
                        <span>حداکثر عمق زنجیره</span>
                        <input
                            type="number"
                            value={values.max_depth}
                            onChange={(e) => update('max_depth', e.target.value)}
                        />
                        <small>حداکثر ۱۰ سطح.</small>
                    </label>
                </div>

                <div className="form-col">
                    <label>
                        <span>حداکثر درصد پرداخت از سود</span>
                        <input
                            type="number"
                            value={values.safety_factor}
                            onChange={(e) => update('safety_factor', e.target.value)}
                            step="0.05"
                        />
                        <small>۰.۵ = ۵۰٪ از حاشیه سود.</small>
                    </label>
                </div>

                <div className="form-col">
                    <label>
                        <span>نحوه توزیع بین سطوح</span>
                        <select
                            value={values.distribution}
                            onChange={(e) => update('distribution', e.target.value)}
                        >
                            <option value="halving">نصف‌شونده</option>
                            <option value="linear">خطی</option>
                            <option value="gradual">نزولی تدریجی</option>
                        </select>
                        <small>روش تقسیم سقف بین سطوح.</small>
                    </label>
                </div>
            </div>

            <ScenarioSelector
                suggestions={suggestions}
                selected={selectedCap}
                onSelect={handleSelectCap}
                showManual={showManual}
                onToggleManual={handleToggleManual}
                manualValue={manualValue}
                onManualChange={handleManualChange}
                minMargin={Number(values.min_margin_percent) || 3}
            />

            {suggestionsLoading && (
                <div className="scenario-loading">در حال محاسبه…</div>
            )}

            <div className="form-actions">
                <button type="submit" disabled={loading} className="btn-primary">
                    {loading ? 'در حال محاسبه...' : 'شبیه‌سازی'}
                </button>
                <button
                    type="button"
                    onClick={handleCompare}
                    disabled={loading}
                    className="btn-secondary"
                >
                    مقایسه مدل‌ها
                </button>
            </div>
        </form>
    );
}